/**
 * AdminCommerceCenter — super-admin-only revenue dashboard.
 *
 * Header tiles show gross revenue, paid orders, refunds, disputes, open
 * VIP cases, active dealers, and grace-period dealers. Below sits a
 * filterable orders table (by product / status / dealer email) and a
 * side panel of open VIP cases with consultation usage.
 *
 * Only fetches — no mutations. Refunds/disputes are still managed inside
 * Stripe directly; this surface is for visibility + support triage.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, DollarSign, ShoppingBag, RotateCcw, ShieldAlert, Crown,
  Users, Clock, Search, RefreshCw, ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAuthToken } from '../utils/auth';
import { PRODUCT_BY_ID } from '../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  paid:     'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending:  'bg-amber-100 text-amber-800 border-amber-200',
  failed:   'bg-rose-100 text-rose-800 border-rose-200',
  refunded: 'bg-slate-200 text-slate-800 border-slate-300',
};

const fmtMoney = (cents, currency = 'usd') => {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const productName = (id) => PRODUCT_BY_ID[id]?.name || id || '—';

const Tile = ({ label, value, sub, Icon, color = 'text-slate-700', bg = 'bg-slate-100', testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{label}</p>
      <p className="text-xl font-black text-slate-900 leading-tight truncate">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const AdminCommerceCenter = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [vipCases, setVipCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ product_id: '', status: '', dealer_email: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const params = {};
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.status) params.status = filters.status;
      if (filters.dealer_email) params.dealer_email = filters.dealer_email;

      const [s, o, v] = await Promise.all([
        axios.get(`${API}/admin/commerce/summary`, { headers }),
        axios.get(`${API}/admin/commerce/orders`, { headers, params: { ...params, limit: 100 } }),
        axios.get(`${API}/admin/commerce/vip-cases`, { headers, params: { status: 'open', limit: 25 } }),
      ]);
      setSummary(s.data);
      setOrders(o.data.orders || []);
      setOrdersTotal(o.data.total || 0);
      setVipCases(v.data.cases || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load commerce data');
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const productOptions = useMemo(() => Object.entries(PRODUCT_BY_ID).map(([id, p]) => (
    <option key={id} value={id}>{p.name}</option>
  )), []);

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16" data-testid="admin-commerce-center">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="commerce-back-btn"
            aria-label="Back to admin"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-black text-slate-900">Commerce Center</h1>
            <p className="text-xs text-slate-500">Every order, refund, dispute, and VIP case in one place.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid="commerce-refresh-btn" className="gap-2">
          <RefreshCw size={16} /> Refresh
        </Button>
      </header>

      {/* Header tiles */}
      <section className="px-6 py-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="commerce-tiles">
        <Tile label="Gross revenue"   value={summary ? fmtMoney(summary.gross_revenue_cents) : '—'} Icon={DollarSign} bg="bg-emerald-100" color="text-emerald-700" testid="tile-gross" />
        <Tile label="Paid orders"     value={summary?.paid_count ?? 0}    Icon={ShoppingBag} bg="bg-blue-100" color="text-blue-700" testid="tile-paid" />
        <Tile label="Refunds"         value={summary?.refunded_count ?? 0} Icon={RotateCcw} bg="bg-slate-200" color="text-slate-700" testid="tile-refunds" />
        <Tile label="Disputes open"   value={summary?.disputes_open ?? 0}  Icon={ShieldAlert} bg="bg-rose-100" color="text-rose-700" testid="tile-disputes" />
        <Tile label="Open VIP cases"  value={summary?.open_vip_cases ?? 0} Icon={Crown} bg="bg-fuchsia-100" color="text-fuchsia-700" testid="tile-vip" />
        <Tile label="Active dealers"  value={summary?.active_dealers ?? 0} Icon={Users} bg="bg-cyan-100" color="text-cyan-700" testid="tile-active-dealers" />
        <Tile label="Grace-period"    value={summary?.grace_dealers ?? 0}  sub="dealers about to lapse" Icon={Clock} bg="bg-amber-100" color="text-amber-700" testid="tile-grace-dealers" />
      </section>

      {/* Orders table + VIP side panel */}
      <div className="px-6 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="orders-panel">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Orders <span className="text-slate-400 font-medium normal-case tracking-normal">({ordersTotal})</span></h2>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filters.product_id}
                onChange={(e) => setFilters(f => ({ ...f, product_id: e.target.value }))}
                className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
                data-testid="orders-filter-product"
              >
                <option value="">All products</option>
                {productOptions}
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
                data-testid="orders-filter-status"
              >
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="disputed">Disputed</option>
              </select>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <input
                  type="email"
                  placeholder="Email…"
                  value={filters.dealer_email}
                  onChange={(e) => setFilters(f => ({ ...f, dealer_email: e.target.value }))}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg pl-7 pr-2 py-1.5 w-40"
                  data-testid="orders-filter-email"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-xs">No orders match the current filters.</td></tr>
                ) : orders.map((o) => (
                  <tr key={o.session_id} data-testid={`order-row-${o.session_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2 whitespace-nowrap text-slate-600 text-xs">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="font-semibold text-slate-800">{productName(o.product_id)}</div>
                      <div className="text-[10px] text-slate-400">{o.product_id}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-slate-700 text-xs">{o.email || '—'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-right font-bold text-slate-900">{fmtMoney(o.amount_total, o.currency)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border capitalize ${STATUS_STYLES[o.payment_status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                        {o.payment_status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {o.dispute_status === 'open' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-rose-100 text-rose-800 border-rose-200 mr-1">
                          <ShieldAlert className="w-3 h-3" /> Disputed
                        </span>
                      )}
                      {o.refund_status === 'refunded' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-slate-200 text-slate-800 border-slate-300 mr-1">
                          <RotateCcw className="w-3 h-3" /> Refunded
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* VIP cases side panel */}
        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="vip-cases-panel">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <Crown className="w-4 h-4 text-fuchsia-600" />
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Open VIP cases</h2>
            <span className="ml-auto text-xs text-slate-400 font-medium">{vipCases.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
            {vipCases.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400">No open VIP cases.</div>
            ) : vipCases.map((c) => {
              const used = c.consultations_used || 0;
              const total = c.consultations_total || 0;
              const daysLeft = c.deadline_at ? Math.max(0, Math.ceil((new Date(c.deadline_at).getTime() - Date.now()) / 86400000)) : null;
              return (
                <div key={c.id} className="p-4" data-testid={`vip-case-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-bold text-slate-800">{productName(c.product_id)}</span>
                    {c.priority === 'high' && (
                      <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 bg-fuchsia-100 border border-fuchsia-200 rounded-full px-2 py-0.5">High</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 truncate">{c.email}</p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                    <span>{used}/{total} calls used</span>
                    {daysLeft != null && <span>· {daysLeft}d left</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminCommerceCenter;
